import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => (
  <div className="min-h-screen bg-black text-white py-16 px-4 sm:px-8">
    {/* Animated Background Elements */}
    <div className="absolute top-[5%] left-[5%] w-[300px] h-[300px] bg-gradient-to-br from-[#e07a5f] to-[#ff6b6b] rounded-full opacity-[0.08] blur-[100px] animate-float" />
    <div className="absolute bottom-[5%] right-[5%] w-[250px] h-[250px] bg-gradient-to-br from-blue-500 to-purple-500 rounded-full opacity-[0.08] blur-[80px] animate-float-reverse" />

    {/* Navigation */}
    <nav className="mb-16 text-center">
      <Link to="/" className="inline-flex items-center gap-2 no-underline">
        <span className="text-4xl">🕵️‍♂️</span>
        <span className="text-2xl font-bold text-[#e07a5f]">WhyDidItDie</span>
      </Link>
    </nav>

    {/* Main Content */}
    <div className="max-w-3xl mx-auto">
      <h1 className="text-4xl sm:text-5xl font-black text-center mb-12 bg-gradient-to-r from-white via-[#e07a5f] to-[#ff6b6b] bg-clip-text text-transparent">
        Privacy Policy
      </h1>

      <div className="bg-white/5 p-6 sm:p-8 rounded-3xl border border-white/10 backdrop-blur-xl">
        <p className="mb-4 text-white/90">
          <strong>Effective Date:</strong> 26th July 2025<br />
          <strong>Last Updated:</strong> 26th July 2025
        </p>

        <p className="mb-8 text-white/80 leading-relaxed">
          WhyDidItDie ("we," "our," "us") is committed to protecting your privacy and ensuring the responsible use of your data. This Privacy Policy explains what information we collect, how we use it, and how we keep it secure when you use our platform.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">1. Information We Collect</h3>
        <p className="mb-4 text-white/90">
          When you connect your advertising accounts (e.g., Meta/Facebook Ads) to WhyDidItDie, we may collect:
        </p>
        <ul className="mb-4 pl-6 list-disc text-white/90">
          <li><strong>Advertising account data:</strong> Campaign names, ad spend, impressions, clicks, CTR, conversions, and related performance metrics.</li>
          <li><strong>Account information:</strong> Your email address, business manager ID, and ad account ID for authentication and account linking.</li>
          <li><strong>Technical data:</strong> Device type, IP address, and usage logs for security and analytics.</li>
        </ul>
        <p className="mb-8 text-white/80">
          We do <strong>not</strong> collect sensitive personal information like passwords, payment card data, or private messages from Meta.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">2. How We Use Your Data</h3>
        <p className="mb-4 text-white/90">
          We collect and use your data to:
        </p>
        <ul className="mb-4 pl-6 list-disc text-white/90">
          <li>Provide campaign analysis and AI-powered budget diagnostics.</li>
          <li>Detect ad performance trends, anomalies, and potential budget leaks.</li>
          <li>Generate reports and insights for your connected ad accounts.</li>
          <li>Maintain and improve the WhyDidItDie platform.</li>
        </ul>
        <p className="mb-8 text-white/80">
          Our AI-powered analytics feature uses <strong>Google Gemini API</strong> to analyze aggregated campaign metrics and suggest optimization opportunities.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">3. Third-Party Data Processors</h3>
        <p className="mb-4 text-white/90">
          To deliver our services, we use trusted third-party service providers that process your data securely:
        </p>
        <ul className="mb-4 pl-6 list-disc text-white/90">
          <li><strong>Supabase Inc. (USA)</strong> – Cloud database hosting and encrypted data storage.</li>
          <li><strong>Google LLC (Ireland)</strong> – Gemini API for AI-driven ad performance analysis.</li>
        </ul>
        <p className="mb-8 text-white/80">
          These providers comply with applicable data protection laws and have appropriate security measures in place.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">4. Legal Basis for Processing</h3>
        <p className="mb-8 text-white/80">
          When you connect your Meta account, you explicitly grant us permission (via OAuth) to access your ad performance data. This connection is voluntary and can be revoked at any time via your Meta Business Integrations or by contacting us.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">5. How We Store and Protect Your Data</h3>
        <ul className="mb-8 pl-6 list-disc text-white/90">
          <li>All advertising data is stored securely in <strong>encrypted databases hosted on Supabase</strong>.</li>
          <li>Access tokens and credentials are stored using industry-standard encryption.</li>
          <li>Access to your data is restricted to you (and authorized support staff, if needed).</li>
          <li>We do not share or sell your data to third parties.</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">6. Data Retention</h3>
        <p className="mb-8 text-white/80">
          We retain your data only for as long as necessary to provide our services or as required by law. You may delete your data at any time via the <strong>Campaign Detective tool</strong> in your dashboard or by emailing <a href="mailto:support@whydiditdie.com" className="text-[#e07a5f] no-underline hover:underline">support@whydiditdie.com</a>.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">7. Your Rights</h3>
        <p className="mb-4 text-white/90">
          You have the right to:
        </p>
        <ul className="mb-8 pl-6 list-disc text-white/90">
          <li>Revoke Meta access at any time via <strong>Business Integrations</strong>.</li>
          <li>Request a copy of the data we hold about you.</li>
          <li>Request correction or deletion of your data.</li>
        </ul>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">8. Sharing with Public Authorities</h3>
        <p className="mb-8 text-white/80">
          We do <strong>not</strong> share your personal or advertising data with public authorities unless required by law or to comply with legal processes.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">9. Changes to this Policy</h3>
        <p className="mb-8 text-white/80">
          We may update this Privacy Policy from time to time. The "Last Updated" date at the top of this page reflects the latest version. Significant changes will be communicated via email or dashboard notifications.
        </p>

        <h3 className="text-2xl font-bold text-[#e07a5f] mt-8 mb-4">10. Contact Us</h3>
        <p className="mb-4 text-white/90">
          If you have questions or concerns about this Privacy Policy or our data practices, contact us at:
        </p>
        <p className="mb-2 text-white/90">
          <strong>Email:</strong> <a href="mailto:support@whydiditdie.com" className="text-[#e07a5f] no-underline hover:underline">support@whydiditdie.com</a>
        </p>

        {/* Social Media Section */}
        <h3 className="text-2xl font-bold text-[#e07a5f] mt-12 mb-6 text-center">Connect With Us</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <a
            href="https://www.instagram.com/ai_whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-6 h-6 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.326 3.608 1.301.975.975 1.24 2.242 1.301 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.326 2.633-1.301 3.608-.975.975-2.242 1.24-3.608 1.301-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.326-3.608-1.301-.975-.975-1.24-2.242-1.301-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.326-2.633 1.301-3.608.975-.975 2.242-1.24 3.608-1.301 1.266-.058 1.646-.07 4.85-.07m0-2.163c-3.259 0-3.667.014-4.947.072-1.524.069-2.978.377-4.103 1.502-1.125 1.125-1.433 2.579-1.502 4.103-.058 1.28-.072 1.688-.072 4.947s.014 3.667.072 4.947c.069 1.524.377 2.978 1.502 4.103 1.125 1.125 2.579 1.433 4.103 1.502 1.28.058 1.688.072 4.947.072s3.667-.014 4.947-.072c1.524-.069 2.978-.377 4.103-1.502 1.125-1.125 1.433-2.579 1.502-4.103.058-1.28.072-1.688.072-4.947s-.014-3.667-.072-4.947c-.069-1.524-.377-2.978-1.502-4.103-1.125-1.125-2.579-1.433-4.103-1.502-1.28-.058-1.688-.072-4.947-.072z"/>
              <path d="M12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zm0 10.162a4 4 0 110-8 4 4 0 010 8z"/>
              <circle cx="18.406" cy="5.594" r="1.44"/>
            </svg>
            <span className="text-white/90 group-hover:text-[#ff6b6b]">@ai_whydiditdie</span>
          </a>
          <a
            href="https://x.com/whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-6 h-6 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zM16.62 20.61h2.048L7.353 3.39H5.154l11.466 17.22z"/>
            </svg>
            <span className="text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
          </a>
          <a
            href="https://www.facebook.com/whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-6 h-6 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span className="text-white/90 group-hover:text-[#ff6b6b]">@whydiditdie</span>
          </a>
          <a
            href="https://www.tiktok.com/@app.whydiditdie"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 p-4 bg-white/10 rounded-lg border border-white/20 hover:bg-[#e07a5f]/20 hover:border-[#e07a5f]/50 transition-all"
          >
            <svg className="w-6 h-6 text-[#e07a5f] group-hover:text-[#ff6b6b]" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69c-.93-.91-2.17-1.49-3.54-1.62v-2.1h-2.64v11.28c0 1.38-1.12 2.5-2.5 2.5s-2.5-1.12-2.5-2.5 1.12-2.5 2.5-2.5c.24 0 .47.04.69.11v-2.88c-.22-.02-.44-.03-.67-.03-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5V8.64c1.33.53 2.81.84 4.31.84V6.69z"/>
            </svg>
            <span className="text-white/90 group-hover:text-[#ff6b6b]">@app.whydiditdie</span>
          </a>
        </div>
      </div>

      {/* Back to Home Link */}
      <div className="text-center mt-8">
        <Link to="https://app.whydiditdie.com" className="text-[#e07a5f] text-lg no-underline hover:underline">
          ← Back to Home
        </Link>
      </div>
    </div>

    {/* Tailwind Animation Styles */}
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0px) rotate(0deg); }
        50% { transform: translateY(-20px) rotate(180deg); }
      }
      .animate-float {
        animation: float 8s ease-in-out infinite;
      }
      .animate-float-reverse {
        animation: float 10s ease-in-out infinite reverse;
      }
      @media (max-width: 640px) {
        .text-4xl {
          font-size: 2rem;
        }
        .max-w-3xl {
          padding-left: 1rem;
          padding-right: 1rem;
        }
        .p-6 {
          padding: 1.5rem;
        }
      }
    `}</style>
  </div>
);

export default PrivacyPolicyPage;